// src/components/Login.jsx
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

const API_BASE = "https://sy47l7fjif.execute-api.us-east-1.amazonaws.com/Prod";
const IMPRESSUM_URL = "https://www.di-inkasso.de/impressum";

const Badge = ({ children, kind = "default" }) => {
  const base =
    "inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium border";
  const styles = {
    default: "bg-gray-100 text-gray-700 border-gray-200",
    blue: "bg-blue-100 text-blue-700 border-blue-200",
    green: "bg-green-100 text-green-700 border-green-200",
    yellow: "bg-yellow-100 text-yellow-700 border-yellow-200",
  };

  return (
    <span className={`${base} ${styles[kind] || styles.default}`}>
      {children}
    </span>
  );
};

const normalizeSlug = (value) =>
  String(value || "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "-")
    .replace(/[^a-z0-9-]/g, "")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "");

const isLegacyManualCompanyId = (value) =>
  String(value || "").startsWith("company_manual_");

const readCompanyContext = () => {
  try {
    return JSON.parse(localStorage.getItem("company_context") || "{}");
  } catch {
    return {};
  }
};

const getCompanyId = () => {
  const direct = String(localStorage.getItem("company_id") || "").trim();
  if (direct) return direct;

  const ctx = readCompanyContext();
  return String(ctx.company_id || "").trim();
};

const getProfileCompleted = () => {
  if (localStorage.getItem("company_profile_completed") === "true") {
    return true;
  }

  const ctx = readCompanyContext();

  if (ctx?.completed === true) {
    return true;
  }

  const hasCompanyId = String(ctx?.company_id || "").trim().length > 0;
  const hasLegalName =
    String(ctx?.legal_name || ctx?.company_name || "").trim().length > 0;

  return hasCompanyId && hasLegalName;
};

const mergeCompanyContext = ({
  companyId = "",
  companyName = "",
  providerType = "",
  providerContextId = "",
  completed = null,
}) => {
  const existing = readCompanyContext();

  const merged = {
    ...existing,
    company_id: companyId || existing.company_id || null,
    legal_name:
      companyName || existing.legal_name || existing.company_name || "",
    company_name:
      companyName || existing.company_name || existing.legal_name || "",
    provider_type: providerType || existing.provider_type || null,
    provider_context_id:
      providerContextId || existing.provider_context_id || null,
    completed:
      typeof completed === "boolean"
        ? completed
        : existing.completed === true
        ? true
        : false,
  };

  localStorage.setItem("company_context", JSON.stringify(merged));

  if (merged.company_id) {
    localStorage.setItem("company_id", merged.company_id);
  }

  if (merged.company_name) {
    localStorage.setItem("company_name", merged.company_name);
  }

  if (merged.provider_type) {
    localStorage.setItem("provider_type", merged.provider_type);
    localStorage.setItem("provider", merged.provider_type);
  }

  if (merged.provider_context_id) {
    localStorage.setItem("provider_context_id", merged.provider_context_id);
  }

  if (merged.completed === true) {
    localStorage.setItem("company_profile_completed", "true");
  }

  return merged;
};

const startCobotOAuth = (workspace) => {
  const normalizedWorkspace = normalizeSlug(workspace);

  if (!normalizedWorkspace) {
    return false;
  }

  const clientId = "86097bc64f1d818fc779f1a3c83a7809";
  const redirectUri = encodeURIComponent(
    "https://a6fvh67b5j.execute-api.us-east-1.amazonaws.com/Prod/store_token"
  );
  const scope = encodeURIComponent(
    "read read_user invoices members list_spaces"
  );
  const state = encodeURIComponent(normalizedWorkspace);

  const authUrl =
    `https://www.cobot.me/oauth/authorize` +
    `?response_type=code` +
    `&client_id=${clientId}` +
    `&redirect_uri=${redirectUri}` +
    `&scope=${scope}` +
    `&state=${state}`;

  console.log("🚀 OAuth state:", normalizedWorkspace);
  console.log("🔗 OAuth URL:", authUrl);

  window.location.href = authUrl;
  return true;
};

const Login = () => {
  const navigate = useNavigate();

  const [workspaceName, setWorkspaceName] = useState("");
  const [loadingQonto, setLoadingQonto] = useState(false);

  const [companyIdInput, setCompanyIdInput] = useState("");
  const [password, setPassword] = useState("");
  const [loginLoading, setLoginLoading] = useState(false);
  const [loginError, setLoginError] = useState("");

  useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search);

    const connectMode = String(
      localStorage.getItem("connect_mode") || ""
    ).trim();

    const token =
      searchParams.get("token") ||
      searchParams.get("access_token") ||
      "";

    const spaceId =
      searchParams.get("SpaceID") ||
      searchParams.get("space_id") ||
      searchParams.get("space_slug") ||
      "";

    const companyIdFromUrl =
      searchParams.get("company_id") ||
      searchParams.get("companyId") ||
      "";

    const companyNameFromUrl =
      searchParams.get("company_name") ||
      searchParams.get("companyName") ||
      "";

    const providerFromUrl = searchParams.get("provider") || "";

    const existingCompanyId = String(
      localStorage.getItem("company_id") || ""
    ).trim();

    const existingToken = String(
      localStorage.getItem("space_token") || ""
    ).trim();

    const existingSpaceId = String(
      localStorage.getItem("space_id") || ""
    ).trim();

    if (isLegacyManualCompanyId(existingCompanyId)) {
      localStorage.removeItem("company_id");
      localStorage.removeItem("company_context");
      localStorage.removeItem("company_profile_completed");
    }

    if (token) {
      localStorage.setItem("space_token", token);
      localStorage.setItem("token", token);
      localStorage.setItem("provider_token", token);
    }

    if (spaceId) {
      localStorage.setItem("space_id", spaceId);
      localStorage.setItem("SpaceID", spaceId);
      localStorage.setItem("provider_context_id", spaceId);
      localStorage.setItem("provider_type", "cobot");
      localStorage.setItem("provider", "cobot");
      localStorage.setItem("connected_provider_cobot", "true");
    }

    if (companyIdFromUrl && !isLegacyManualCompanyId(companyIdFromUrl)) {
      localStorage.setItem("company_id", companyIdFromUrl);
    }

    if (companyNameFromUrl) {
      localStorage.setItem("company_name", companyNameFromUrl);
    }

    if (providerFromUrl) {
      localStorage.setItem("provider", providerFromUrl);
      localStorage.setItem("provider_type", providerFromUrl);

      if (providerFromUrl === "cobot") {
        localStorage.setItem("connected_provider_cobot", "true");
      }

      if (providerFromUrl === "qonto") {
        localStorage.setItem("connected_provider_qonto", "true");
      }
    } else if (token && spaceId) {
      localStorage.setItem("provider_type", "cobot");
      localStorage.setItem("provider", "cobot");
      localStorage.setItem("connected_provider_cobot", "true");
    }

    const effectiveCompanyId = getCompanyId();
    const effectiveCompanyName = String(
      companyNameFromUrl || localStorage.getItem("company_name") || ""
    ).trim();

    const effectiveProviderType = String(
      providerFromUrl ||
        localStorage.getItem("provider_type") ||
        (spaceId || token ? "cobot" : "")
    ).trim();

    const effectiveProviderContextId = String(
      spaceId ||
        localStorage.getItem("provider_context_id") ||
        localStorage.getItem("space_id") ||
        ""
    ).trim();

    if (
      effectiveCompanyId ||
      effectiveCompanyName ||
      effectiveProviderType ||
      effectiveProviderContextId
    ) {
      mergeCompanyContext({
        companyId: effectiveCompanyId,
        companyName: effectiveCompanyName,
        providerType: effectiveProviderType,
        providerContextId: effectiveProviderContextId,
        completed: getProfileCompleted(),
      });
    }

    if (token || spaceId || companyIdFromUrl || companyNameFromUrl || providerFromUrl) {
      localStorage.removeItem("continue_without_provider");
      window.history.replaceState({}, document.title, window.location.pathname);
    }

    const finalToken = token || existingToken;
    const finalSpaceId = spaceId || existingSpaceId;
    const finalCompanyId = getCompanyId();
    const finalProfileCompleted = getProfileCompleted();
    const isOAuthReturn = !!(token || spaceId || providerFromUrl);

    if (connectMode === "attach_cobot" && !isOAuthReturn) {
      const reconnectWorkspace =
        localStorage.getItem("provider_context_id") ||
        localStorage.getItem("space_id") ||
        localStorage.getItem("SpaceID") ||
        "";

      if (reconnectWorkspace) {
        startCobotOAuth(reconnectWorkspace);
        return;
      }
    }

    if (finalCompanyId && finalProfileCompleted) {
      navigate("/company-dashboard", { replace: true });
      return;
    }

    if (finalCompanyId && (finalToken || finalSpaceId || providerFromUrl)) {
      navigate("/company-dashboard", { replace: true });
      return;
    }

    if (finalCompanyId && !finalProfileCompleted) {
      navigate("/profile", { replace: true });
      return;
    }

    if (!finalCompanyId && finalToken && finalSpaceId) {
      navigate("/profile", { replace: true });
    }
  }, [navigate]);

  const resetProviderSession = () => {
    localStorage.removeItem("space_token");
    localStorage.removeItem("space_id");
    localStorage.removeItem("token");
    localStorage.removeItem("SpaceID");

    localStorage.removeItem("provider_token");
    localStorage.removeItem("provider_type");
    localStorage.removeItem("provider");
    localStorage.removeItem("provider_context_id");

    localStorage.removeItem("connected_provider_cobot");
    localStorage.removeItem("connected_provider_qonto");

    localStorage.removeItem("continue_without_provider");
  };

  const clearFullSessionForFreshStart = () => {
    resetProviderSession();

    localStorage.removeItem("company_id");
    localStorage.removeItem("company_name");
    localStorage.removeItem("company_profile_completed");
    localStorage.removeItem("company_context");
    localStorage.removeItem("return_to");
    localStorage.removeItem("connect_mode");
  };

  const handleContinueWithoutProvider = () => {
    const normalizedWorkspace = normalizeSlug(workspaceName);

    clearFullSessionForFreshStart();
    localStorage.setItem("continue_without_provider", "true");

    if (normalizedWorkspace) {
      localStorage.setItem("provider_context_id", normalizedWorkspace);
    }

    if (workspaceName.trim()) {
      localStorage.setItem("company_name", workspaceName.trim());
    }

    navigate("/profile");
  };

  const handleExistingCompanyLogin = async (e) => {
    e.preventDefault();
    setLoginError("");

    const normalizedCompanyId = normalizeSlug(companyIdInput);

    if (!normalizedCompanyId) {
      setLoginError("Bitte eine Company ID eingeben.");
      return;
    }

    if (!password.trim()) {
      setLoginError("Bitte ein Passwort eingeben.");
      return;
    }

    try {
      setLoginLoading(true);

      const res = await fetch(`${API_BASE}/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          company_id: normalizedCompanyId,
          password: password,
        }),
      });

      const data = await res.json();

      if (!res.ok || data?.ok !== true) {
        setLoginError(data?.error || "Login fehlgeschlagen.");
        return;
      }

      const company = data?.company || {};
      const profileCompleted = data?.profile_completed === true;
      const providerType = data?.provider_type || company?.provider_type || "";
      const providerContextId =
        data?.provider_context_id || company?.provider_context_id || "";

      resetProviderSession();
      localStorage.removeItem("return_to");
      localStorage.removeItem("connect_mode");

      localStorage.setItem(
        "company_id",
        company.company_id || normalizedCompanyId
      );

      if (company.legal_name || company.company_name) {
        localStorage.setItem(
          "company_name",
          company.legal_name || company.company_name
        );
      }

      localStorage.setItem(
        "company_profile_completed",
        profileCompleted ? "true" : "false"
      );

      if (providerType) {
        localStorage.setItem("provider_type", providerType);
        localStorage.setItem("provider", providerType);
      }

      if (providerContextId) {
        localStorage.setItem("provider_context_id", providerContextId);
      }

      mergeCompanyContext({
        companyId: company.company_id || normalizedCompanyId,
        companyName: company.legal_name || company.company_name || "",
        providerType,
        providerContextId,
        completed: profileCompleted,
      });

      navigate(profileCompleted ? "/company-dashboard" : "/profile", {
        replace: true,
      });
    } catch (err) {
      console.error("Login Fehler:", err);
      setLoginError(
        "Login aktuell nicht verfügbar. Bitte prüfe, ob der /login Endpoint bereits existiert."
      );
    } finally {
      setLoginLoading(false);
    }
  };

  const handleLogoutLocalSession = () => {
    localStorage.removeItem("company_id");
    localStorage.removeItem("company_name");
    localStorage.removeItem("company_profile_completed");
    localStorage.removeItem("company_context");
    localStorage.removeItem("return_to");
    localStorage.removeItem("connect_mode");

    resetProviderSession();

    setCompanyIdInput("");
    setPassword("");
    setLoginError("");
  };

  return (
    <div className="min-h-screen bg-gray-50 text-gray-900">
      <div className="border-b border-gray-200 bg-white/95 backdrop-blur">
        <div className="mx-auto flex max-w-7xl items-center justify-between gap-4 px-6 py-5">
          <div className="flex items-center gap-4">
            <img src="/logo.png" alt="DI Inkasso" className="h-10" />
            <div>
              <div className="text-2xl font-bold tracking-tight text-gray-900">
                Inkasso Plattform
              </div>
              <div className="text-sm text-gray-500">
                Digitales Debitorenmanagement für Mandanten und Admins
              </div>
            </div>
          </div>

          <div className="hidden md:flex items-center gap-2">
            <Badge kind="green">Cobot möglich</Badge>
            <Badge kind="blue">Qonto möglich</Badge>
            <Badge kind="yellow">Mandantenfähig</Badge>
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-7xl px-6 py-10">
        <div className="overflow-hidden rounded-[2rem] bg-gradient-to-r from-slate-950 via-slate-900 to-slate-800 text-white shadow-xl">
          <div className="px-6 py-10 md:px-10 md:py-12">
            <div className="flex flex-wrap items-center gap-2">
              <Badge kind="default">Debitorenmanagement</Badge>
              <Badge kind="green">Cashflow im Blick</Badge>
              <Badge kind="blue">Zentral gesteuert</Badge>
            </div>

            <h1 className="mt-5 max-w-4xl text-3xl font-bold leading-tight md:text-5xl">
              Offene Forderungen früher erkennen. Prozesse bündeln. Cashflow
              smarter absichern.
            </h1>

            <p className="mt-5 max-w-3xl text-sm leading-7 text-slate-300 md:text-base">
              Die Inkasso Connector Plattform hilft Unternehmen dabei, ihr
              Debitorenmanagement strukturiert und digital aufzusetzen. Mandanten
              erhalten einen klaren Einstieg, eine zentrale Sicht auf künftige
              Datenquellen und einen sauberen Übergang in die weitere
              Inkasso-Steuerung.
            </p>

            <div className="mt-8 grid grid-cols-1 gap-4 md:grid-cols-3">
              <div className="rounded-2xl border border-white/10 bg-white/5 p-4">
                <div className="text-sm font-semibold text-white">
                  1. Einstieg
                </div>
                <p className="mt-2 text-sm text-slate-300">
                  Bestehende Firmen loggen sich ein. Neue Mandanten starten mit
                  ihrer Company-Erstanlage.
                </p>
              </div>

              <div className="rounded-2xl border border-white/10 bg-white/5 p-4">
                <div className="text-sm font-semibold text-white">
                  2. Datenquellen
                </div>
                <p className="mt-2 text-sm text-slate-300">
                  Verfügbare Provider wie Cobot oder Qonto werden zentral
                  unterstützt und später im Dashboard verbunden.
                </p>
              </div>

              <div className="rounded-2xl border border-white/10 bg-white/5 p-4">
                <div className="text-sm font-semibold text-white">
                  3. Steuerung
                </div>
                <p className="mt-2 text-sm text-slate-300">
                  Forderungen prüfen, priorisieren und für die nächste Stufe im
                  Prozess vorbereiten.
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-8 grid grid-cols-1 gap-8 xl:grid-cols-5">
          <div className="xl:col-span-2">
            <div className="rounded-3xl border border-gray-200 bg-white p-6 shadow-sm">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <h2 className="text-xl font-semibold text-gray-900">
                    Login für bestehende Firmen
                  </h2>
                  <p className="mt-2 text-sm text-gray-500">
                    Für Mandanten mit bestehender Company ID und Passwort.
                  </p>
                </div>
                <Badge kind="blue">Bestandslogin</Badge>
              </div>

              <form onSubmit={handleExistingCompanyLogin} className="mt-6">
                <div>
                  <label className="mb-2 block text-sm font-medium text-gray-700">
                    Company ID
                  </label>
                  <input
                    type="text"
                    value={companyIdInput}
                    onChange={(e) => setCompanyIdInput(e.target.value)}
                    placeholder="z. B. p-ds-gmbh"
                    className="w-full rounded-xl border border-gray-300 p-3 text-gray-900 placeholder-gray-400 focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-200"
                  />
                </div>

                <div className="mt-4">
                  <label className="mb-2 block text-sm font-medium text-gray-700">
                    Passwort
                  </label>
                  <input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Passwort eingeben"
                    className="w-full rounded-xl border border-gray-300 p-3 text-gray-900 placeholder-gray-400 focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-200"
                  />
                </div>

                {loginError ? (
                  <div className="mt-4 rounded-2xl border border-red-100 bg-red-50 p-4 text-sm text-red-700">
                    {loginError}
                  </div>
                ) : null}

                <button
                  type="submit"
                  disabled={loginLoading}
                  className="mt-5 w-full rounded-xl bg-blue-600 px-4 py-3 text-sm font-semibold text-white transition hover:bg-blue-700 disabled:opacity-60"
                >
                  {loginLoading
                    ? "Einloggen..."
                    : "Als bestehende Firma einloggen"}
                </button>
              </form>

              <button
                type="button"
                onClick={handleLogoutLocalSession}
                className="mt-3 w-full rounded-xl border border-gray-300 px-4 py-3 text-sm font-medium text-gray-700 transition hover:bg-gray-50"
              >
                Lokale Sitzung zurücksetzen
              </button>

              <div className="mt-5 rounded-2xl border border-gray-200 bg-gray-50 p-4">
                <div className="text-sm font-medium text-gray-900">Hinweis</div>
                <p className="mt-1 text-sm leading-6 text-gray-600">
                  Diese Login-Variante ist die aktuelle Zwischenstufe vor Auth0.
                  Die Oberfläche kann später bestehen bleiben, während die
                  Authentifizierung im Hintergrund modernisiert wird.
                </p>
              </div>
            </div>
          </div>

          <div className="xl:col-span-3 space-y-6">
            <div className="rounded-3xl border border-gray-200 bg-white p-6 shadow-sm">
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div>
                  <h2 className="text-xl font-semibold text-gray-900">
                    Erstanlage Company
                  </h2>
                  <p className="mt-2 text-sm text-gray-500">
                    Für neue Mandanten. Die Verbindung zu Providern erfolgt
                    später im Dashboard.
                  </p>
                </div>
                <Badge kind="yellow">Neuer Start</Badge>
              </div>

              <div className="mt-6">
                <label className="mb-2 block text-sm font-medium text-gray-700">
                  Firmenname, Arbeitsname oder interner Kontext
                </label>
                <input
                  type="text"
                  value={workspaceName}
                  onChange={(e) => setWorkspaceName(e.target.value)}
                  placeholder="z. B. Hello Cowork oder Direkt Inkasso"
                  className="w-full rounded-xl border border-gray-300 p-3 text-gray-900 placeholder-gray-400 focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-200"
                />
              </div>

              <div className="mt-5 rounded-2xl border border-amber-100 bg-amber-50 p-4">
                <div className="text-sm font-medium text-amber-900">
                  Smart Start
                </div>
                <p className="mt-1 text-sm leading-6 text-amber-800">
                  Zuerst wird die Company angelegt. Datenquellen wie
                  Cobot oder Qonto werden bewusst erst im Dashboard verbunden,
                  damit Einstieg, Mandant und spätere Provider-Zuordnung klar
                  getrennt bleiben.
                </p>
              </div>

              <div className="mt-6 flex flex-col gap-3 sm:flex-row">
                <button
                  type="button"
                  onClick={handleContinueWithoutProvider}
                  className="w-full rounded-xl bg-blue-600 px-4 py-3 text-sm font-semibold text-white transition hover:bg-blue-700 sm:w-auto"
                >
                  Company jetzt anlegen
                </button>

                <button
                  type="button"
                  onClick={handleContinueWithoutProvider}
                  className="w-full rounded-xl border border-gray-300 px-4 py-3 text-sm font-medium text-gray-700 transition hover:bg-gray-50 sm:w-auto"
                >
                  Ohne Tool-Verbindung fortfahren
                </button>
              </div>
            </div>

            <div className="rounded-3xl border border-gray-200 bg-white p-6 shadow-sm">
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">
                    Mögliche Provider
                  </h3>
                  <p className="mt-2 text-sm text-gray-500">
                    Diese Quellen können perspektivisch mit dem Mandanten
                    verbunden werden. Die technische Verbindung erfolgt im
                    Dashboard.
                  </p>
                </div>
                <Badge kind="default">Übersicht</Badge>
              </div>

              <div className="mt-5 grid grid-cols-1 gap-4 md:grid-cols-3">
                <div className="rounded-2xl border border-gray-200 bg-gray-50 p-5">
                  <div className="flex items-center justify-between gap-3">
                    <div className="text-base font-semibold text-gray-900">
                      Cobot
                    </div>
                    <Badge kind="green">integriert</Badge>
                  </div>
                  <p className="mt-3 text-sm leading-6 text-gray-600">
                    Geeignet für Coworking Spaces, Rechnungen,
                    Mahnlogik und bestehende space-basierte Prozesse.
                  </p>
                </div>

                <div className="rounded-2xl border border-gray-200 bg-gray-50 p-5">
                  <div className="flex items-center justify-between gap-3">
                    <div className="text-base font-semibold text-gray-900">
                      Qonto
                    </div>
                    <Badge kind="blue">nächster Schritt</Badge>
                  </div>
                  <p className="mt-3 text-sm leading-6 text-gray-600">
                    Perspektivisch für Unternehmen, die Rechnungen und
                    Inkasso-Kandidaten aus ihrem Finanz-Setup zentral verwalten
                    möchten.
                  </p>
                </div>

                <div className="rounded-2xl border border-gray-200 bg-gray-50 p-5">
                  <div className="flex items-center justify-between gap-3">
                    <div className="text-base font-semibold text-gray-900">
                      Weitere Quellen
                    </div>
                    <Badge kind="yellow">coming soon</Badge>
                  </div>
                  <p className="mt-3 text-sm leading-6 text-gray-600">
                    Die Plattform ist mandantenfähig angelegt und kann später
                    um weitere Datenquellen und Provider erweitert werden.
                  </p>
                </div>
              </div>
            </div>

            <div className="rounded-3xl border border-gray-200 bg-white p-6 shadow-sm">
              <h3 className="text-lg font-semibold text-gray-900">
                Wie geht es danach weiter?
              </h3>

              <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-3">
                <div className="rounded-2xl border border-gray-200 bg-gray-50 p-4">
                  <div className="text-sm font-semibold text-gray-800">
                    1. Company-Profil
                  </div>
                  <p className="mt-2 text-sm leading-6 text-gray-500">
                    Unternehmensdaten vervollständigen, Pflichtangaben erfassen
                    und AGB bestätigen.
                  </p>
                </div>

                <div className="rounded-2xl border border-gray-200 bg-gray-50 p-4">
                  <div className="text-sm font-semibold text-gray-800">
                    2. Provider im Dashboard
                  </div>
                  <p className="mt-2 text-sm leading-6 text-gray-500">
                    Passende Quellen wie Cobot oder später Qonto gezielt mit dem
                    Mandanten verbinden.
                  </p>
                </div>

                <div className="rounded-2xl border border-gray-200 bg-gray-50 p-4">
                  <div className="text-sm font-semibold text-gray-800">
                    3. Forderungen steuern
                  </div>
                  <p className="mt-2 text-sm leading-6 text-gray-500">
                    Offene Posten prüfen, priorisieren und strukturiert in den
                    nächsten Inkasso-Prozess überführen.
                  </p>
                </div>
              </div>
            </div>

            <div className="rounded-3xl border border-dashed border-gray-300 bg-gradient-to-br from-white to-gray-50 p-6 shadow-sm">
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">
                    Coming soon
                  </h3>
                  <p className="mt-2 text-sm text-gray-500">
                    Weitere Hilfen und Services für einen noch klareren
                    Forderungsprozess.
                  </p>
                </div>
                <Badge kind="yellow">in Planung</Badge>
              </div>

              <div className="mt-5 grid grid-cols-1 gap-4 md:grid-cols-2">
                <div className="rounded-2xl border border-gray-200 bg-white p-4">
                  <div className="text-sm font-semibold text-gray-800">
                    FAQ zu Inkasso
                  </div>
                  <p className="mt-2 text-sm leading-6 text-gray-500">
                    Antworten auf typische Fragen zu Ablauf, Übergabe, Status
                    und den nächsten Schritten im Forderungsmanagement.
                  </p>
                </div>

                <div className="rounded-2xl border border-gray-200 bg-white p-4">
                  <div className="text-sm font-semibold text-gray-800">
                    Reminder Service
                  </div>
                  <p className="mt-2 text-sm leading-6 text-gray-500">
                    Ergänzende Erinnerungs- und Vorstufenlogik für ein moderneres
                    Debitorenmanagement mit besserer Übersicht und mehr Timing.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-10 flex flex-col items-center justify-center gap-2 text-center text-xs text-gray-400">
          <div>
            DI Direkt Inkasso GmbH · Mandantenplattform für strukturiertes
            Debitorenmanagement
          </div>
          <a
            href={IMPRESSUM_URL}
            target="_blank"
            rel="noreferrer"
            className="font-medium text-gray-500 underline underline-offset-4 hover:text-gray-700"
          >
            Impressum
          </a>
        </div>
      </div>
    </div>
  );
};

export default Login;